import React, { useState, useEffect } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { ThemeColors } from '../theme.ts';

interface RulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dos: string[], donts: string[]) => void;
  onUpdate?: (id: string, dos: string[], donts: string[]) => void;
  theme: ThemeColors;
  campaignId: string;
  existingRules?: {
    id: string;
    dos: string[];
    donts: string[];
  } | null;
  isEditMode?: boolean;
}

const RulesModal: React.FC<RulesModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onUpdate,
  theme, 
  campaignId,
  existingRules,
  isEditMode = false
}) => {
  const [dosRules, setDosRules] = useState<string[]>([]);
  const [dontsRules, setDontsRules] = useState<string[]>([]);
  const [newDoRuleInput, setNewDoRuleInput] = useState("");
  const [newDontRuleInput, setNewDontRuleInput] = useState("");
  const [ruleErrors, setRuleErrors] = useState({ doRule: '', dontRule: '' });

  // Initialize form with existing rules when in edit mode
  useEffect(() => {
    if (isEditMode && existingRules) {
      setDosRules(existingRules.dos || []);
      setDontsRules(existingRules.donts || []);
    } else {
      // Reset for add mode
      setDosRules([]);
      setDontsRules([]);
      setNewDoRuleInput("");
      setNewDontRuleInput("");
    }
  }, [isEditMode, existingRules, isOpen]);

  const handleAddDoRule = () => {
    if (newDoRuleInput.trim()) {
      setDosRules([...dosRules, newDoRuleInput.trim()]);
      setNewDoRuleInput("");
      if (ruleErrors.doRule) {
        setRuleErrors(prev => ({ ...prev, doRule: '' }));
      }
    }
  };

  const handleAddDontRule = () => {
    if (newDontRuleInput.trim()) {
      setDontsRules([...dontsRules, newDontRuleInput.trim()]);
      setNewDontRuleInput("");
      if (ruleErrors.dontRule) {
        setRuleErrors(prev => ({ ...prev, dontRule: '' }));
      }
    }
  };

  const handleRemoveDoRule = (index: number) => {
    const newRules = [...dosRules];
    newRules.splice(index, 1);
    setDosRules(newRules);
  };

  const handleRemoveDontRule = (index: number) => {
    const newRules = [...dontsRules];
    newRules.splice(index, 1);
    setDontsRules(newRules);
  };

  const handleSave = () => {
    // Validation: Check if at least one rule exists
    if (dosRules.length === 0 && dontsRules.length === 0) {
      setRuleErrors({ 
        doRule: 'At least one rule is required', 
        dontRule: 'At least one rule is required' 
      });
      return;
    }

    if (isEditMode && existingRules && onUpdate) {
      onUpdate(existingRules.id, dosRules, dontsRules);
    } else {
      onSave(dosRules, dontsRules);
    }
    
    // Reset form after save
    setDosRules([]);
    setDontsRules([]);
    setNewDoRuleInput("");
    setNewDontRuleInput("");
    setRuleErrors({ doRule: '', dontRule: '' });
  };

  const handleClose = () => {
    // Reset form when closing
    setDosRules([]);
    setDontsRules([]);
    setNewDoRuleInput("");
    setNewDontRuleInput("");
    setRuleErrors({ doRule: '', dontRule: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[103]" style={{ backgroundColor: theme.background + 'CC' }}>
      <div className="rounded-lg shadow-xl w-full max-w-xl border-2 max-h-[90vh] flex flex-col" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
        {/* Modal Header */}
        <div className="p-4 border-b" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold" style={{ color: theme.primary }}>
              {isEditMode ? "Edit Rules" : "Add Rules"}
            </h3>
            <button
              onClick={handleClose}
              className="transition-colors"
              style={{ color: theme.textSecondary }}
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-4 overflow-y-auto flex-grow">
          <div className="space-y-6">
            {/* Campaign ID Display */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>
                Campaign ID
              </label>
              <div
                className="w-full p-2 border rounded text-sm"
                style={{
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderColor: theme.border,
                }}
              >
                {campaignId}
              </div>
            </div>

            {/* Do's Section */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.textSecondary }}>
                Do's Rules
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Enter a Do rule"
                  value={newDoRuleInput}
                  onChange={(e) => setNewDoRuleInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddDoRule();
                    }
                  }}
                  className="flex-1 p-2 border rounded text-sm focus:ring-2"
                  style={{
                    backgroundColor: theme.surface,
                    color: theme.text,
                    borderColor: ruleErrors.doRule ? theme.error : theme.border,
                    caretColor: theme.text,
                  }}
                />
                <button
                  onClick={handleAddDoRule}
                  className="px-3 rounded flex items-center gap-1 text-white"
                  style={{ backgroundColor: theme.primary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.secondary || theme.primary}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.primary}
                >
                  <FaPlus size={12} />
                </button>
              </div>

              {dosRules.length > 0 && (
                <div className="rounded p-2 max-h-32 overflow-y-auto" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
                  <div className="flex justify-between items-center p-2">
                    <span className="text-sm font-medium" style={{ color: theme.primary }}>
                      Your Do's Rules:
                    </span>
                    <span className="text-xs" style={{ color: theme.textSecondary }}>
                      {dosRules.length} added
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 p-2">
                    {dosRules.map((rule: string, index: number) => (
                      <div
                        key={index}
                        className="px-3 py-2 rounded-lg text-sm flex items-center"
                        style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
                      >
                        <span className="flex-grow" style={{ color: theme.text }}>{rule}</span>
                        <button
                          onClick={() => handleRemoveDoRule(index)}
                          className="ml-2"
                          style={{ color: theme.error }}
                          type="button"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ruleErrors.doRule && (
                <p className="text-sm mt-1" style={{ color: theme.error }}>{ruleErrors.doRule}</p>
              )}
            </div>

            {/* Don'ts Section */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.textSecondary }}>
                Don'ts Rules
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Enter a Don't rule"
                  value={newDontRuleInput}
                  onChange={(e) => {
                    setNewDontRuleInput(e.target.value);
                    if (ruleErrors.dontRule) {
                      setRuleErrors(prev => ({ ...prev, dontRule: '' }));
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddDontRule();
                    }
                  }}
                  className="flex-1 p-2 border rounded text-sm focus:ring-2"
                  style={{
                    backgroundColor: theme.surface,
                    color: theme.text,
                    borderColor: ruleErrors.dontRule ? theme.error : theme.border,
                    caretColor: theme.text,
                  }}
                />
                <button
                  onClick={handleAddDontRule}
                  className="px-3 rounded flex items-center gap-1 text-white"
                  style={{ backgroundColor: theme.primary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.secondary || theme.primary}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.primary}
                >
                  <FaPlus size={12} />
                </button>
              </div>

              {dontsRules.length > 0 && (
                <div className="rounded p-2 max-h-32 overflow-y-auto" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
                  <div className="flex justify-between items-center p-2">
                    <span className="text-sm font-medium" style={{ color: theme.primary }}>
                      Your Don'ts Rules:
                    </span>
                    <span className="text-xs" style={{ color: theme.textSecondary }}>
                      {dontsRules.length} added
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 p-2">
                    {dontsRules.map((rule: string, index: number) => (
                      <div
                        key={index}
                        className="px-3 py-2 rounded-lg text-sm flex items-center"
                        style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
                      >
                        <span className="flex-grow" style={{ color: theme.text }}>{rule}</span>
                        <button
                          onClick={() => handleRemoveDontRule(index)}
                          className="ml-2"
                          style={{ color: theme.error }}
                          type="button"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ruleErrors.dontRule && (
                <p className="text-sm mt-1" style={{ color: theme.error }}>{ruleErrors.dontRule}</p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t flex justify-end space-x-2" style={{ borderTop: `1px solid ${theme.border}` }}>
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded text-sm"
            style={{
              color: theme.textSecondary,
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded text-sm text-white transition-colors"
            style={{ backgroundColor: theme.primary }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.secondary || theme.primary}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.primary}
          >
            {isEditMode ? "Update Rules" : "Save Rules"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RulesModal;